import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../assets/spacem8-logo.png";
import { supabase } from "../lib/supabase";
import { formatMoney } from "../lib/currency";
import { useToast } from "../contexts/ToastContext";
import {
  BrainCircuit,
  Building2,
  ChartNoAxesCombined,
  FileText,
  House,
  MessageSquareMore,
  Moon,
  Search,
  Send,
  Settings,
  Sparkles,
  Sun,
  Users,
  Wallet,
  Wrench,
  X,
} from "lucide-react";

function Button({ children, className = "", variant = "default", ...props }) {
  const base =
    "inline-flex items-center justify-center whitespace-nowrap transition-all font-medium disabled:pointer-events-none disabled:opacity-50";

  const styles =
    variant === "outline"
      ? "border"
      : "bg-orange-500 text-white hover:bg-orange-400 shadow-sm";

  return (
    <button className={`${base} ${styles} ${className}`} {...props}>
      {children}
    </button>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

const navItems = [
  { label: "Dashboard", icon: House, path: "/" },
  { label: "Properties", icon: Building2, path: "/properties" },
  { label: "Tenants", icon: Users, path: "/tenants" },
  { label: "Payments", icon: Wallet, path: "/payments" },
  { label: "Invoices", icon: FileText, path: "/invoices" },
  { label: "Maintenance", icon: Wrench, path: "/maintenance" },
  { label: "Messages", icon: MessageSquareMore, path: "/messages" },
  { label: "Reports", icon: ChartNoAxesCombined, path: "/reports" },
  { label: "AI Assistant", icon: BrainCircuit, path: "/ai-assistant" },
  { label: "Settings", icon: Settings, path: "/settings" },
];

function createConversationId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `conversation-${Date.now()}`;
}

function formatMessageTime(value) {
  if (!value) return "";

  return new Intl.DateTimeFormat("en-KE", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function mapMessageFromSupabase(message) {
  return {
    id: message.id,
    conversationId: message.conversation_id,
    contactName: message.contact_name,
    contactRole: message.contact_role,
    direction: message.direction,
    body: message.body,
    createdAt: message.created_at,
  };
}

function getTenantBalanceKey(name, property) {
  return `${String(name || "").trim().toLowerCase()}|${String(property || "")
    .trim()
    .toLowerCase()}`;
}

function getPropertyFromRole(role) {
  return String(role || "")
    .replace(/^tenant\s*-\s*/i, "")
    .trim();
}

function escapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function replaceDraftText(body, previousValue, nextValue) {
  if (!body || !previousValue || !nextValue || previousValue === nextValue) return body;
  return body.replace(new RegExp(escapeRegExp(previousValue), "g"), nextValue);
}

function personalizeDraftBody(body, previousContactName, nextContactName, previousProperty, nextProperty) {
  if (!body || !nextContactName) return body;

  let nextBody = body;

  if (previousContactName) {
    nextBody = replaceDraftText(nextBody, previousContactName, nextContactName);
  } else if (/^hello,\s*/i.test(nextBody)) {
    nextBody = nextBody.replace(/^hello,\s*/i, `Hello ${nextContactName}, `);
  }

  if (previousProperty && nextProperty) {
    nextBody = replaceDraftText(nextBody, previousProperty, nextProperty);
  }

  return nextBody;
}

function buildTenantBalanceMap(invoices = [], payments = []) {
  const paidByInvoice = payments.reduce((totals, payment) => {
    if (payment.invoice_id && payment.status === "Paid") {
      totals[payment.invoice_id] = (totals[payment.invoice_id] || 0) + Number(payment.amount || 0);
    }

    return totals;
  }, {});

  return invoices.reduce((balances, invoice) => {
    const amount = Number(invoice.amount || 0);
    const paidAmount =
      invoice.status === "Paid" && !paidByInvoice[invoice.id]
        ? amount
        : Number(paidByInvoice[invoice.id] || 0);
    const balance = Math.max(amount - paidAmount, 0);

    if (!invoice.tenant || balance <= 0) return balances;

    const key = getTenantBalanceKey(invoice.tenant, invoice.property);
    const existing = balances[key] || {
      balance: 0,
      property: invoice.property || "their property",
      dueDate: invoice.due_date || "",
    };

    balances[key] = {
      ...existing,
      balance: existing.balance + balance,
      dueDate: existing.dueDate || invoice.due_date || "",
    };

    return balances;
  }, {});
}

function buildTenantPaymentReminder(tenant, balanceSummary, currency = "KES") {
  const property = tenant.property_name || "your property";

  if (balanceSummary?.balance > 0) {
    return `Hello ${tenant.full_name}, this is a friendly reminder that ${formatMoney(
      balanceSummary.balance,
      currency
    )} is still outstanding for ${property}. Please arrange payment as soon as possible, or contact management if you need help confirming your balance. Thank you.`;
  }

  return `Hello ${tenant.full_name}, this is a friendly check-in regarding your rent/payment account for ${property}. Please contact management if you need help confirming your current balance. Thank you.`;
}

function buildConversations(messages) {
  const grouped = new Map();

  messages.forEach((message) => {
    const existing = grouped.get(message.conversationId);

    if (!existing || new Date(message.createdAt) > new Date(existing.lastCreatedAt)) {
      grouped.set(message.conversationId, {
        id: message.conversationId,
        name: message.contactName,
        role: message.contactRole,
        lastMessage: message.body,
        lastCreatedAt: message.createdAt,
        time: formatMessageTime(message.createdAt),
      });
    }
  });

  return Array.from(grouped.values()).sort(
    (a, b) => new Date(b.lastCreatedAt) - new Date(a.lastCreatedAt)
  );
}

function Sidebar({ isDark }) {
  return (
    <aside
      className={`hidden xl:flex xl:w-72 xl:flex-col xl:rounded-[2rem] xl:border xl:p-5 xl:shadow-sm ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-6 px-2">
        <div
          className={`flex items-center justify-center rounded-3xl border px-6 py-6 shadow-sm transition-all hover:shadow-md ${
            isDark
              ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
              : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
          }`}
        >
          <img
            src={logo}
            alt="SpaceM8"
            className="h-20 w-auto cursor-pointer object-contain transition-all duration-300 hover:scale-110"
            onClick={() => (window.location.href = "/")}
          />
        </div>

        <p className={`mt-4 text-center text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          Communication Hub
        </p>
      </div>

      <nav className="mt-1 space-y-2">
        {navItems.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.label}
              to={item.path}
              className={({ isActive }) =>
                `flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all ${
                  isActive
                    ? isDark
                      ? "bg-slate-800 text-white ring-1 ring-slate-700"
                      : "bg-sky-50 text-slate-900 ring-1 ring-sky-100"
                    : isDark
                    ? "text-slate-300 hover:bg-slate-800 hover:text-white"
                    : "text-slate-600 hover:bg-gradient-to-br hover:from-white hover:to-sky-50 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`h-5 w-5 ${
                      isActive
                        ? isDark
                          ? "text-sky-400"
                          : "text-sky-700"
                        : isDark
                        ? "text-slate-500"
                        : "text-slate-400"
                    }`}
                  />
                  <span className="text-sm font-medium">{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}

function NewConversationModal({ isOpen, onClose, onCreated, isDark, initialDraft, currency }) {
  const [tenants, setTenants] = useState([]);
  const [tenantBalances, setTenantBalances] = useState({});
  const [form, setForm] = useState({
    tenantId: "",
    contactName: "",
    contactRole: "",
    body: "",
  });
  const [saving, setSaving] = useState(false);
  const draftKey = initialDraft
    ? [initialDraft.draftId, initialDraft.draftType, initialDraft.contactName, initialDraft.contactRole, initialDraft.body].join("|")
    : "manual";

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      tenantId: "",
      contactName: initialDraft?.contactName || "",
      contactRole: initialDraft?.contactRole || "",
      body: initialDraft?.body || "",
    });
    setSaving(false);
  }, [
    isOpen,
    draftKey,
    initialDraft?.body,
    initialDraft?.contactName,
    initialDraft?.contactRole,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const fetchMessageContext = async () => {
      const [{ data: tenantData, error: tenantError }, { data: invoiceData, error: invoiceError }, { data: paymentData, error: paymentError }] =
        await Promise.all([
          supabase.from("tenants").select("id, full_name, property_name").order("full_name", { ascending: true }),
          supabase.from("invoices").select("id, tenant, property, amount, status, due_date"),
          supabase.from("payments").select("invoice_id, amount, status"),
        ]);

      if (cancelled) return;

      if (tenantError) {
        console.error("Error fetching tenants for messages:", tenantError);
        setTenants([]);
      } else {
        setTenants(tenantData || []);
      }

      if (invoiceError) console.error("Error fetching invoice balances for messages:", invoiceError);
      if (paymentError) console.error("Error fetching payment balances for messages:", paymentError);

      setTenantBalances(buildTenantBalanceMap(invoiceData || [], paymentData || []));
    };

    fetchMessageContext();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const inputClass = isDark
    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
    : "border-slate-200 bg-white text-slate-900";

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleTenantChange = (tenantId) => {
    const tenant = tenants.find((item) => item.id === tenantId);
    const nextProperty = tenant?.property_name || "No property";
    const balanceKey = tenant ? getTenantBalanceKey(tenant.full_name, tenant.property_name) : "";

    setForm((prev) => ({
      ...prev,
      tenantId,
      contactName: tenant?.full_name || "",
      contactRole: tenant ? `Tenant - ${nextProperty}` : "",
      body:
        tenant && initialDraft?.draftType === "payment-reminder"
          ? buildTenantPaymentReminder(tenant, tenantBalances[balanceKey], currency)
          : personalizeDraftBody(
              prev.body,
              prev.contactName,
              tenant?.full_name || "",
              getPropertyFromRole(prev.contactRole),
              tenant ? nextProperty : ""
            ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const wasCreated = await onCreated({
      conversationId: createConversationId(),
      contactName: form.contactName,
      contactRole: form.contactRole || "Contact",
      body: form.body,
    });

    setSaving(false);

    if (wasCreated) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              New Message
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Start a tenant, vendor, or property conversation.
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4">
          <Select
            value={form.tenantId}
            onChange={(e) => handleTenantChange(e.target.value)}
            className={inputClass}
          >
            <option value="">Select tenant or enter manually</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.full_name} - {tenant.property_name}
              </option>
            ))}
          </Select>

          <Input
            placeholder="Contact name"
            value={form.contactName}
            onChange={(e) => update("contactName", e.target.value)}
            className={inputClass}
            required
          />

          <Input
            placeholder="Role or context"
            value={form.contactRole}
            onChange={(e) => update("contactRole", e.target.value)}
            className={inputClass}
          />

          <textarea
            value={form.body}
            onChange={(e) => update("body", e.target.value)}
            placeholder="Message"
            className={`min-h-[130px] w-full rounded-2xl border px-4 py-3 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${inputClass}`}
            required
          />

          <div className="mt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MessagesPage({ isDark, setIsDark, currency = "KES" }) {
  const location = useLocation();
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [query, setQuery] = useState("");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [draftMessage, setDraftMessage] = useState(null);

  const fetchMessages = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching messages:", error);
      setMessages([]);
    } else {
      setMessages((data || []).map(mapMessageFromSupabase));
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    const nextDraft = location.state?.draftMessage;

    if (!nextDraft) return;

    setDraftMessage({
      ...nextDraft,
      draftId: nextDraft.draftId || `draft-${Date.now()}`,
    });
    setIsNewOpen(true);
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const conversations = useMemo(() => buildConversations(messages), [messages]);

  useEffect(() => {
    if (!selectedId && conversations.length > 0) {
      setSelectedId(conversations[0].id);
      return;
    }

    if (selectedId && conversations.length > 0 && !conversations.some((item) => item.id === selectedId)) {
      setSelectedId(conversations[0].id);
    }
  }, [conversations, selectedId]);

  const filteredConversations = useMemo(() => {
    const search = query.toLowerCase();

    return conversations.filter(
      (conversation) =>
        conversation.name.toLowerCase().includes(search) ||
        conversation.role.toLowerCase().includes(search) ||
        conversation.lastMessage.toLowerCase().includes(search)
    );
  }, [conversations, query]);

  const selected = conversations.find((item) => item.id === selectedId) || null;

  const selectedMessages = useMemo(() => {
    if (!selected) return [];

    return messages.filter((message) => message.conversationId === selected.id);
  }, [messages, selected]);

  const insertMessage = async ({ conversationId, contactName, contactRole, body }) => {
    const payload = {
      conversation_id: conversationId,
      contact_name: contactName,
      contact_role: contactRole,
      direction: "me",
      body,
    };

    const { data, error } = await supabase
      .from("messages")
      .insert([payload])
      .select()
      .single();

    if (error) {
      console.error("Send message error:", error);
      toast.error("Message send failed", "Please try sending your message again.");
      return false;
    }

    const savedMessage = mapMessageFromSupabase(data);
    setMessages((prev) => [...prev, savedMessage]);
    setSelectedId(savedMessage.conversationId);
    toast.success("Message sent", `Your message to ${savedMessage.contactName} is now saved.`);
    return true;
  };

  const handleSend = async (e) => {
    e.preventDefault();

    if (!selected || !input.trim()) return;

    setSending(true);

    const wasSent = await insertMessage({
      conversationId: selected.id,
      contactName: selected.name,
      contactRole: selected.role,
      body: input.trim(),
    });

    setSending(false);

    if (wasSent) setInput("");
  };

  return (
    <div
      className={`min-h-screen ${
        isDark
          ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white"
          : "bg-gradient-to-br from-[#f8fbff] via-[#eef6ff] to-[#f3f9ff] text-slate-900"
      }`}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={`absolute inset-0 ${
            isDark
              ? "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.08),_transparent_22%),linear-gradient(to_bottom_right,_#020617,_#0f172a,_#020617)]"
              : "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.10),_transparent_24%),linear-gradient(to_bottom_right,_#f8fbff,_#f6f9fc,_#eef5fb)]"
          }`}
        />
      </div>

      <div className="relative mx-auto flex max-w-[1600px] gap-6 p-4 md:p-6 xl:p-8">
        <Sidebar isDark={isDark} />

        <main className="flex-1">
          <motion.header
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-[2rem] border p-5 shadow-sm ${
              isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sky-700">
                  <Sparkles className="h-4 w-4" />
                  <p className="text-xs font-semibold uppercase tracking-[0.2em]">
                    Communication Center
                  </p>
                </div>
                <h1 className={`mt-2 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                  Messages
                </h1>
                <p className={`mt-2 max-w-2xl text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Manage tenant, vendor, and property communication in one executive conversation workspace.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  className={`h-12 rounded-2xl px-4 ${
                    isDark
                      ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  onClick={() => setIsDark(!isDark)}
                >
                  {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                  {isDark ? "Light" : "Dark"}
                </Button>

                <Button
                  onClick={() => {
                    setDraftMessage(null);
                    setIsNewOpen(true);
                  }}
                  className="h-12 rounded-2xl px-5"
                >
                  <MessageSquareMore className="mr-2 h-4 w-4" />
                  New Message
                </Button>
              </div>
            </div>
          </motion.header>

          <section className="mt-6 grid gap-6 xl:grid-cols-[360px_1fr]">
            <div
              className={`rounded-[2rem] border p-4 shadow-md ${
                isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className="mb-4">
                <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  Conversations
                </h2>
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className={`h-12 pl-11 pr-4 ${
                    isDark
                      ? "border-slate-700 bg-slate-900 text-white placeholder:text-slate-400"
                      : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
                  }`}
                />
              </div>

              <div className="space-y-3">
                {loading ? (
                  <div className={`rounded-2xl border p-4 text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-600"}`}>
                    Loading conversations...
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className={`rounded-2xl border p-4 text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-600"}`}>
                    No conversations found.
                  </div>
                ) : (
                  filteredConversations.map((conversation) => (
                    <button
                      key={conversation.id}
                      onClick={() => setSelectedId(conversation.id)}
                      className={`w-full rounded-2xl border p-4 text-left transition ${
                        selected?.id === conversation.id
                          ? "border-sky-200 bg-sky-50 text-slate-900"
                          : isDark
                          ? "border-slate-800 bg-slate-950/50 hover:bg-slate-800"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className={`break-words font-medium ${isDark && selected?.id !== conversation.id ? "text-white" : "text-slate-900"}`}>
                            {conversation.name}
                          </p>
                          <p className={`mt-1 text-xs ${isDark && selected?.id !== conversation.id ? "text-slate-400" : "text-slate-500"}`}>
                            {conversation.role}
                          </p>
                          <p className={`mt-2 break-words text-sm ${isDark && selected?.id !== conversation.id ? "text-slate-300" : "text-slate-600"}`}>
                            {conversation.lastMessage}
                          </p>
                        </div>
                        <span className={`shrink-0 text-xs ${isDark && selected?.id !== conversation.id ? "text-slate-500" : "text-slate-400"}`}>
                          {conversation.time}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div
              className={`rounded-[2rem] border shadow-md ${
                isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className={`border-b p-5 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
                <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {selected?.name || "No conversation selected"}
                </h2>
                <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  {selected?.role || "Start a new message to begin."}
                </p>
              </div>

              <div className="min-h-[420px] space-y-4 p-5">
                {selectedMessages.length === 0 ? (
                  <div className={`rounded-2xl border p-5 text-center text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-600"}`}>
                    No messages in this conversation yet.
                  </div>
                ) : (
                  selectedMessages.map((message, index) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: index * 0.03 }}
                      className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-6 ${
                        message.direction === "me"
                          ? "ml-auto bg-orange-500 text-white"
                          : isDark
                          ? "bg-slate-800 text-slate-200"
                          : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      <p>{message.body}</p>
                      <p className={`mt-2 text-[11px] ${message.direction === "me" ? "text-orange-100" : isDark ? "text-slate-500" : "text-slate-400"}`}>
                        {formatMessageTime(message.createdAt)}
                      </p>
                    </motion.div>
                  ))
                )}
              </div>

              <form onSubmit={handleSend} className={`border-t p-5 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
                <div className="flex gap-3">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={selected ? "Type a message..." : "Select a conversation first"}
                    disabled={!selected}
                    className={`h-12 flex-1 ${
                      isDark
                        ? "border-slate-700 bg-slate-900 text-white placeholder:text-slate-400"
                        : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
                    }`}
                  />
                  <Button type="submit" disabled={!selected || sending} className="h-12 rounded-2xl px-5">
                    <Send className="mr-2 h-4 w-4" />
                    {sending ? "Sending..." : "Send"}
                  </Button>
                </div>
              </form>
            </div>
          </section>
        </main>
      </div>

      <NewConversationModal
        isOpen={isNewOpen}
        onClose={() => {
          setIsNewOpen(false);
          setDraftMessage(null);
        }}
        onCreated={insertMessage}
        isDark={isDark}
        initialDraft={draftMessage}
        currency={currency}
      />
    </div>
  );
}
